import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import anime from "animejs"
import classNames from "classnames"
import { noop } from "lodash-es"
import { AnimatePresence, usePresence } from "motion/react"
import { create } from "zustand"

import { useTranslation } from "@/locales"
import Close from "@/topic/rogue_5/components/Close"
import { getStartOfDay } from "@/topic/rogue_5/utils/misc"
import { SFX } from "@/topic/rogue_5/utils/sfx"
import { useGameStore } from "../../../store"

import styles from "./index.module.less"

const SCENE_ANIMATION = {
    animate: async (container: HTMLDivElement) => {
        // const buttonGroup = container.querySelector(
        //     `.${styles.buttonGroup}`
        // ) as HTMLDivElement
        const layer2 = container.querySelector(
            `.${styles.layer2}`
        ) as HTMLDivElement
        const layer3 = container.querySelector(
            `.${styles.layer3}`
        ) as HTMLDivElement
        const contentContainer = container.querySelector(
            `.${styles.contentContainer}`
        ) as HTMLDivElement
        const label = container.querySelector(
            `.${styles.label}`
        ) as HTMLDivElement
        const coins = container.querySelectorAll(
            `.${styles.coinOut}`
        ) as NodeListOf<HTMLDivElement>

        const timeline = anime.timeline()
        timeline.add({
            targets: container,
            opacity: [0, 1],
            duration: 300,
            easing: "easeOutCubic",
        })
        const base = {
            opacity: [0, 1],
            duration: 800,
            easing: "easeOutCubic",
            translateY: ["30%", "0"],
            rotate: ["-6deg", "0deg"],
        }
        timeline.add({ targets: layer2, ...base }, 0)
        timeline.add({ targets: layer3, ...base }, 100)
        timeline.add({ targets: contentContainer, ...base }, 200)
        timeline.add({ targets: label, ...base }, 300)

        timeline.add(
            {
                targets: coins,
                scale: [1.2, 1],
                duration: 800,
                delay: anime.stagger(200),
                easing: "easeOutBounce",
            },
            500
        )
        timeline.add(
            {
                targets: coins,
                opacity: [0, 1],
                duration: 400,
                delay: anime.stagger(200),
                easing: "easeOutCubic",
            },
            500
        )
        // timeline.add(
        //     {
        //         targets: buttonGroup,
        //         opacity: [0, 1],
        //         translateY: ["50%", "0"],
        //         translateX: ["-50%", "-50%"],
        //         duration: 500,
        //         easing: "easeOutCubic",
        //         complete: () => {
        //             buttonGroup.style.pointerEvents = "auto"
        //         },
        //     },
        //     1000
        // )
        return timeline.finished
    },
    exit: async (container: HTMLDivElement) => {
        return anime({
            targets: container,
            opacity: [1, 0],
            // filter: ["blur(0px)", "blur(10px)"],
            // scale: [1, 0.9],
            duration: 300,
            easing: "cubicBezier(0.33, 1, 0.68, 1)",
        }).finished
    },
}

const ContentItem: React.FC<{
    className?: string
    style?: React.CSSProperties
    text: string
    open: boolean
}> = ({ className, style, text, open }) => {
    const displayText = text === "ina" ? "in a" : text
    return (
        <div
            className={classNames(
                styles.contentItem,
                open && styles.open,
                // text.length > 3 && styles.small,
                // text.length > 6 && styles.mini,
                className
            )}
            style={style}
        >
            <span className={styles.char}>{displayText}</span>
        </div>
    )
}

const AWARD_IDS = ["GOLD", "COMP", "CARD_EXP"]

const RecordModal: React.FC<{
    className?: string
    style?: React.CSSProperties
    onClose?: () => void
}> = ({ className, style, onClose }) => {
    const { t } = useTranslation()
    const [isPresent, safeToRemove] = usePresence()
    const containerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!containerRef.current) return
        if (isPresent) {
            SCENE_ANIMATION.animate(containerRef.current)
        } else {
            SCENE_ANIMATION.exit(containerRef.current).then(() => {
                // console.log("exit")
                safeToRemove()
            })
        }
    }, [isPresent])

    const {
        info: { signInRecord = [] },
    } = useGameStore()
    const needSignIn = useMemo(() => {
        if (signInRecord.length >= 7) return false
        const lastSignIn =
            signInRecord.slice().sort((a, b) => b.ts - a.ts)[0]?.ts ?? 0
        if (lastSignIn) {
            const startOfDay = getStartOfDay()
            if (lastSignIn < startOfDay) {
                return true
            } else {
                return false
            }
        }
        return true
    }, [])
    const currentStamp = useMemo(() => {
        if (needSignIn) {
            return undefined
        } else {
            return AWARD_IDS.findIndex(
                (id) =>
                    id ===
                    signInRecord.slice().sort((a, b) => b.index - a.index)[0]
                        .rewardId
            )
        }
    }, [])
    return (
        <div
            className={classNames(styles.sceneContainer, className)}
            style={style}
            ref={containerRef}
        >
            <div className={styles.mainFrame}>
                <div className={styles.layer2}></div>
                <div className={styles.layer3}></div>
                <div className={styles.contentContainer}>
                    <div className={styles.card}>
                        <div className={styles.itemTitle}>
                            <div className={styles.item}>
                                <div className={styles.coin}></div>
                                <div className={styles.itemLabel}>
                                    {t("game.record.gift.gold")}
                                </div>
                            </div>
                            <div className={styles.item}>
                                <div className={styles.comp}></div>
                                <div
                                    className={classNames(
                                        styles.itemLabel,
                                        styles.small
                                    )}
                                >
                                    {t("game.record.gift.comp")}
                                </div>
                            </div>
                            <div className={styles.item}>
                                <div className={styles.expCard}></div>
                                <div
                                    className={classNames(
                                        styles.itemLabel,
                                        styles.small
                                    )}
                                >
                                    {t("game.record.gift.card_exp")}
                                </div>
                            </div>
                        </div>
                        <div className={styles.dateTitle}>
                            {t("number")
                                .split("")
                                .slice(0, 7)
                                .map((c, index) => (
                                    <div key={index} className={styles.item}>
                                        {t("game.record.date").replace("$", c)}
                                    </div>
                                ))}
                        </div>
                        <div className={styles.stampContainer}>
                            <div className={styles.stampContainer}>
                                <div className={styles.item}>
                                    <div
                                        className={classNames(
                                            styles.stamp,
                                            currentStamp === 0 && styles.active
                                        )}
                                    ></div>
                                </div>
                                <div className={styles.item}>
                                    <div
                                        className={classNames(
                                            styles.stamp,
                                            currentStamp === 1 && styles.active
                                        )}
                                    ></div>
                                </div>
                                <div className={styles.item}>
                                    <div
                                        className={classNames(
                                            styles.stamp,
                                            currentStamp === 2 && styles.active
                                        )}
                                    ></div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.content}>
                            {t("game.record.content")
                                .split("\n")
                                .map((s, row) => (
                                    <div className={styles.line} key={row}>
                                        {s
                                            .split(" ")
                                            .filter((c) => c !== " ")
                                            .slice(0, 7)
                                            .map((c, column) => {
                                                return (
                                                    <ContentItem
                                                        key={`${c}-${column}`}
                                                        text={c}
                                                        open={
                                                            signInRecord.find(
                                                                (item) =>
                                                                    item.index ===
                                                                    column + 1
                                                            )?.rewardId ===
                                                            AWARD_IDS[row]
                                                        }
                                                    />
                                                )
                                            })}
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
                <div className={styles.label}></div>
                <div className={classNames(styles.coinOut, styles.coin1)}></div>
                <div className={classNames(styles.coinOut, styles.coin2)}></div>
                <div className={classNames(styles.coinOut, styles.coin3)}></div>
            </div>
            <Close className={styles.close} onClick={onClose} />
        </div>
    )
}

const useRecordModalStore = create<{
    activate: (onExit?: () => void) => void
}>(() => ({
    activate: noop,
}))

export const useRecordModal = () =>
    useRecordModalStore((state) => state.activate)

const RecordModalWrapper: React.FC = () => {
    const [active, setActive] = useState(false)
    useEffect(() => {
        useRecordModalStore.setState({
            activate: (onExit) => {
                SFX.play("recordModal")
                setActive(true)
            },
        })
    }, [])
    const onClose = useCallback(() => {
        setActive(false)
    }, [])
    return (
        <AnimatePresence>
            {active && <RecordModal onClose={onClose} />}
        </AnimatePresence>
    )
}

export default RecordModalWrapper
